











































































































































import Vue from 'vue'
import { Component, Prop, Watch } from 'vue-property-decorator';
import { BusyObject } from '@/models/Busy';
import { AgentExecutionFrequency } from '@/models/AgentExecutionFrequency';
import { StoredBrandQueriesService, StoredBrandQuery, User } from '@/api/braendz';
import { BrandQueryMode } from '@/models/Query';
import { Locale, findLocale, locales } from '@/models/Locale';

import Popup from '@/components/Popups/Popup.vue';

@Component({
    components: {
        Popup
    },
    model: {
        prop: "model",
        event: "visibilityChanged"
    }
})
export default class ConfigureStoredBrandQueryPopup extends Vue {
    // Member
    public visibility = false;

    public valid = false;
    public selectedTab = 0;
    public busyStoredBrandQuery = new BusyObject<StoredBrandQuery>();

    public name = "";
    public description: string | null = null;
    public agentExecutionFrequency: AgentExecutionFrequency | null = null;

    public ownerMailAddress: string | null = null;
    public selectedAdditionalMailAddresses: string[] = [];
    public additionalMailAddresses: string[] = [];
    
    public selectedSize = 50;
    public sizes = [ 10, 25, 50, 75, 100 ];
        
    // Properties
    @Prop({ required: false })
    public model?: boolean;

    @Prop({required: true})
    public storedBrandQuery!: StoredBrandQuery;

    // Getter & Setter
    public get visible(): boolean {
        return this.model ?? this.visibility;
    }
    public set visible(value: boolean) {
        this.visibility = value;
        this.$emit('visibilityChanged', value);
    }

    public get defaultName(): string {
        switch(this.storedBrandQuery.settings.query.mode) {
            case BrandQueryMode.Default:
                return this.storedBrandQuery.settings.query.terms?.length === 1 ? this.storedBrandQuery.settings.query.terms[0].value ?? '' : '';
            case BrandQueryMode.Exact:
                return this.storedBrandQuery.settings.query.terms?.map(i => i.field ? `${i.field} ${this.$t('configureStoredBrandQueryPopup.is')} ${i.value ?? this.$t('configureStoredBrandQueryPopup.empty')}` : `${i.value  ?? this.$t('configureStoredBrandQueryPopup.empty')}`).join(` ${this.$t('configureStoredBrandQueryPopup.or')} `) ?? "";
        }
        return "";
    }

    public get userMailAddress(): string | null | undefined {
        return (this.$store.state.user as BusyObject<User>)?.object?.mailAddress;
    }

    public selectedNotificationLanguage: Locale | null = null;

    public get locales(): Locale[] {
      return locales;
    }

    // Watcher:
    @Watch('storedBrandQuery')
    public onStoredBrandQueryChanged() {
        this.reset();
    }

    // Lifecycle Methods:
    public mounted() {
        if(this.userMailAddress) {
            this.additionalMailAddresses = [];
            this.additionalMailAddresses.push(this.userMailAddress);
        }
    }
    

    // Methods:
    public async save(): Promise<void> {
        if (!((this.$refs.form as Vue & { validate: () => boolean }).validate())) {
            return;
        }

        if(!this.ownerMailAddress) {
            return;
        }

        // Clone the existing query settings and modify its size and skip properites:
        const settings = {...this.storedBrandQuery.settings};
        settings.query.limit = this.selectedSize;
        settings.query.skip = null;

        // Modify new object:
        this.busyStoredBrandQuery.object = {
            id: this.storedBrandQuery.id,
            settings: settings,
            name: this.name,
            ownerMailAddress: this.ownerMailAddress,
            description: this.description,
            notificationLanguage: this.selectedNotificationLanguage?.locale,
            agentExecutionFrequency: this.agentExecutionFrequency,
            additionalMailAddresses: this.selectedAdditionalMailAddresses
        };

        await this.busyStoredBrandQuery.create(async () => {
            if(!this.busyStoredBrandQuery.object) {
                return null;
            }
            return await StoredBrandQueriesService.upsertStoredBrandQueryCurrentUserDefaultAccount(this.busyStoredBrandQuery.object);
        });

        if(this.busyStoredBrandQuery.object) {
            this.fireChanged(this.busyStoredBrandQuery.object);
        }

        this.close();
    }

    public removeMailAddress(item: string) {
        this.selectedAdditionalMailAddresses.splice(this.selectedAdditionalMailAddresses.indexOf(item), 1);
    }

    public mailAddressesValid(value: []): true | string {
        if(!this.agentExecutionFrequency) {
            return true;
        }

        if(!value) {
            return true;
        }
        
        for(const mail of value) {
            const validationResult = this.$validation.mail(mail as string, '');
            if(validationResult !== true) {
                return validationResult;
            }
        }
        
        return true;
    }

    public reset(): void {

        if(this.storedBrandQuery.id) {
            this.name = this.storedBrandQuery.name    
        }
        else {
            if(this.storedBrandQuery.name) {
                this.name = this.storedBrandQuery.name
            }
            else {
                this.name = this.defaultName;
            }
        }
        
        this.description = this.storedBrandQuery.description ?? null;

        this.selectedSize = this.storedBrandQuery.settings.query.limit ?? this.storedBrandQuery.settings.query.size ?? this.sizes[2];

        this.selectedNotificationLanguage = findLocale(this.storedBrandQuery.notificationLanguage) ?? findLocale(this.$i18n.locale) ?? findLocale('en-GB') ?? locales[0];

        this.ownerMailAddress = this.storedBrandQuery.ownerMailAddress ?? this.userMailAddress;

        if(this.storedBrandQuery.agentExecutionFrequency) {
            this.agentExecutionFrequency = this.storedBrandQuery.agentExecutionFrequency as AgentExecutionFrequency;
        }
        else {
            this.agentExecutionFrequency = null;
        }

        this.selectedAdditionalMailAddresses = this.storedBrandQuery.additionalMailAddresses ?? [];

        this.selectedTab = 0;
    }

    public close(): void {
        this.visible = false;
    }

    public fireChanged(value: StoredBrandQuery): void {
        this.$emit('changed', value);
    }
}
